import {
  AGENT_RUN_STATUS_LABELS,
  CONVERSATION_OUTCOME_LABELS,
  CONVERSATION_OUTCOMES,
  roleAtLeast,
  type AgentRunView,
  type ConversationOutcome,
  type MessageView,
} from '@ai-sales/shared'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { Link, useParams } from 'react-router'
import { fetchChannels, fetchConversationAgent, fetchConversationRuns } from '../api/agents.api.js'
import {
  fetchConversation,
  fetchMessages,
  sendOperatorMessage,
  setHandoff,
  setOutcome,
  simulateInbound,
  updateConversation,
} from '../api/messaging.api.js'
import { Badge } from '../components/badge.js'
import { Button } from '../components/button.js'
import { ErrorPanel } from '../components/error-panel.js'
import { Field } from '../components/field.js'
import { PageHeader, Panel } from '../components/page.js'
import { Spinner } from '../components/spinner.js'
import { cn } from '../lib/cn.js'
import { formatDateTime, formatRelative, plural } from '../lib/format.js'
import { useCurrentWorkspace } from '../workspace/workspace-context.js'

export function ConversationPage() {
  const workspace = useCurrentWorkspace()
  const { conversationId = '' } = useParams()
  const queryClient = useQueryClient()

  const canAct = roleAtLeast(workspace.role, 'operator')

  const conversation = useQuery({
    queryKey: ['conversation', workspace.id, conversationId],
    queryFn: () => fetchConversation(workspace.id, conversationId),
    refetchInterval: 8_000,
  })

  const messages = useInfiniteQuery({
    queryKey: ['messages', workspace.id, conversationId],
    queryFn: ({ pageParam }) =>
      fetchMessages(workspace.id, conversationId, {
        limit: 50,
        ...(pageParam ? { cursor: pageParam } : {}),
      }),
    initialPageParam: '',
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    /**
     * Переписка обновляется сама, пока открыта только первая страница.
     * Догрузив историю, человек читает её — и подменять содержимое под
     * ним нельзя, иначе он теряет место.
     */
    refetchInterval: (q) => ((q.state.data?.pages.length ?? 1) > 1 ? false : 8_000),
  })

  const agent = useQuery({
    queryKey: ['conversation-agent', workspace.id, conversationId],
    queryFn: () => fetchConversationAgent(workspace.id, conversationId),
    /**
     * Пока версия не закреплена — переспрашиваем.
     *
     * Версия закрепляется за диалогом в тот миг, когда агент берётся за
     * первое сообщение, то есть через несколько секунд после его прихода.
     * Открыв только что начатый диалог, человек попадает ровно в этот
     * промежуток и читает «агент в этом диалоге не работал» — и это
     * звучит как поломка, хотя ответ уже в пути. Закрепившись, версия
     * не меняется до конца разговора, поэтому опрос сам и прекращается.
     */
    refetchInterval: (q) => (q.state.data?.agentVersionId ? false : 5_000),
  })

  const channels = useQuery({
    queryKey: ['channels', workspace.id],
    queryFn: () => fetchChannels(workspace.id),
  })

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['conversation', workspace.id, conversationId] }),
      queryClient.invalidateQueries({ queryKey: ['messages', workspace.id, conversationId] }),
      queryClient.invalidateQueries({ queryKey: ['conversations', workspace.id] }),
      queryClient.invalidateQueries({ queryKey: ['conversation-agent', workspace.id, conversationId] }),
    ])

  const setPaused = useMutation({
    mutationFn: (isPaused: boolean) =>
      updateConversation(workspace.id, conversationId, { isPaused }),
    onSuccess: () => void refresh(),
  })

  const setState = useMutation({
    mutationFn: (state: 'open' | 'closed') =>
      updateConversation(workspace.id, conversationId, { state }),
    onSuccess: () => void refresh(),
  })

  const handoff = useMutation({
    mutationFn: (input: { needsOperator: boolean; reason: string }) =>
      setHandoff(workspace.id, conversationId, input),
    onSuccess: () => void refresh(),
  })

  const outcome = useMutation({
    mutationFn: (input: { outcome: ConversationOutcome | null; note: string }) =>
      setOutcome(workspace.id, conversationId, input),
    onSuccess: () => void refresh(),
  })

  const reply = useMutation({
    mutationFn: (text: string) => sendOperatorMessage(workspace.id, conversationId, text),
    onSuccess: () => void refresh(),
  })

  const asCustomer = useMutation({
    mutationFn: (input: { channelId: string; phone: string; text: string }) =>
      simulateInbound(workspace.id, input.channelId, { phone: input.phone, text: input.text }),
    onSuccess: () => void refresh(),
  })

  /**
   * Шаги грузятся только когда их открыли.
   *
   * Переписку читают каждый день, шаги — когда что-то пошло не так.
   * Грузить их всегда значило бы на каждом открытии диалога тянуть из
   * базы десяток записей с журналами, которые никто не смотрит.
   */
  const [showRuns, setShowRuns] = useState(false)

  const runs = useQuery({
    queryKey: ['conversation-runs', workspace.id, conversationId],
    queryFn: () => fetchConversationRuns(workspace.id, conversationId),
    enabled: showRuns,
    refetchInterval: showRuns ? 8_000 : false,
  })

  const [draft, setDraft] = useState('')
  const [testDraft, setTestDraft] = useState('')
  const [handoffReason, setHandoffReason] = useState('')
  const [outcomeNote, setOutcomeNote] = useState('')

  if (conversation.isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted">
        <Spinner /> Загружаем диалог…
      </div>
    )
  }

  if (conversation.error || !conversation.data) {
    return <ErrorPanel error={conversation.error} onRetry={() => void conversation.refetch()} />
  }

  const data = conversation.data
  const channel = channels.data?.find((item) => item.id === data.channelId)
  const isInternal = channel?.provider === 'internal'

  /** Сообщения приходят от новых к старым — читаются сверху вниз. */
  const thread = [...(messages.data?.pages.flatMap((page) => page.items) ?? [])].reverse()

  function submitReply(event: FormEvent) {
    event.preventDefault()
    if (!draft.trim()) return
    reply.mutate(draft.trim(), { onSuccess: () => setDraft('') })
  }

  function submitTest(event: FormEvent) {
    event.preventDefault()
    if (!testDraft.trim() || !channel) return

    asCustomer.mutate(
      { channelId: channel.id, phone: data.contact.phoneE164, text: testDraft.trim() },
      { onSuccess: () => setTestDraft('') },
    )
  }

  return (
    <>
      <PageHeader
        title={data.contact.name || data.contact.phoneE164}
        description={
          <>
            <span className="font-mono">{data.contact.phoneE164}</span> · {data.channelName} ·{' '}
            <Link to={`/w/${workspace.id}/conversations`} className="text-accent hover:underline">
              ко всем диалогам
            </Link>
          </>
        }
        actions={
          <div className="flex flex-wrap items-center gap-1.5">
            {data.needsOperator && <Badge tone="warn">ждёт оператора</Badge>}
            {data.isPaused ? <Badge tone="neutral">на паузе</Badge> : <Badge tone="ok">агент отвечает</Badge>}
            {data.state === 'closed' && <Badge tone="neutral">закрыт</Badge>}
          </div>
        }
      />

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="flex flex-col gap-5">
          <Panel
            title="Переписка"
            description={
              data.isPaused
                ? `На паузе${data.pausedByEmail ? `, поставил ${data.pausedByEmail}` : ''} — агент не отвечает`
                : 'Агент отвечает сам. Чтобы написать руками, диалог встанет на паузу'
            }
            actions={
              messages.hasNextPage && (
                <Button
                  size="sm"
                  variant="ghost"
                  loading={messages.isFetchingNextPage}
                  onClick={() => void messages.fetchNextPage()}
                >
                  Загрузить раньше
                </Button>
              )
            }
          >
            {messages.isLoading ? (
              <p className="flex items-center gap-2 text-sm text-muted">
                <Spinner /> Загружаем сообщения…
              </p>
            ) : thread.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted">Сообщений пока нет.</p>
            ) : (
              <ol className="flex flex-col gap-2.5">
                {thread.map((message) => (
                  <MessageBubble key={message.id} message={message} />
                ))}
              </ol>
            )}
          </Panel>

          {/*
            Тест-чат стоит выше ответа оператора, и это не вкусовщина.
            Обе формы видны только на внутреннем канале, и в проверке
            агента из них двоих раз за разом нужна именно эта: реплика
            клиента. Стояла она второй — и реплика клиента уходила
            в верхнее поле, то есть клиенту от имени оператора, а диалог
            вставал на паузу. Случилось у ANR 24.09.2026 посреди прогона.
            На настоящем канале тест-чата нет вовсе, и ответ оператора
            остаётся единственной формой — то есть первой.
          */}
          {canAct && isInternal && data.state === 'open' && (
            <Panel
              title="Тест-чат: написать как клиент"
              description="Только для внутреннего канала. Сообщение пройдёт тем же путём, каким пойдёт настоящее из WhatsApp — приём, проверки, очередь."
              actions={<Badge tone="neutral">наружу не уходит</Badge>}
            >
              <form onSubmit={submitTest} className="flex flex-col gap-3">
                <Field
                  label="Сообщение от имени клиента"
                  value={testDraft}
                  onChange={(event) => setTestDraft(event.target.value)}
                  maxLength={4096}
                  placeholder="Hola, quiero el producto"
                />

                {asCustomer.error != null && <ErrorPanel error={asCustomer.error} />}

                {asCustomer.data && (
                  <p className="rounded-md border border-line bg-raised px-3 py-2 text-xs text-muted">
                    {asCustomer.data.queued
                      ? 'Принято, задача агенту поставлена.'
                      : `Принято, но агент не вызван: ${asCustomer.data.skipReason ?? 'причина не указана'}`}
                  </p>
                )}

                <div>
                  <Button type="submit" variant="primary" loading={asCustomer.isPending}>
                    Отправить от имени клиента
                  </Button>
                </div>
              </form>
            </Panel>
          )}

          {/*
            Ответ оператора выделен цветом — тем же, которым подписаны
            его реплики в переписке. Две формы подряд с одинаковой
            рамкой и одинаковой зелёной кнопкой различались только
            заголовком, а заголовок при беглой работе не читают.
          */}
          {canAct && data.state === 'open' && (
            <Panel
              className="border-copper/40 border-l-4 border-l-copper bg-copper-soft"
              title="Ответить от себя, как оператор"
              description="Реплика уйдёт клиенту от вашего имени и поставит диалог на паузу: вы вмешались, значит агент замолкает. После снятия паузы агент увидит её вместе с остальной историей."
              actions={<Badge tone="warn">уйдёт клиенту</Badge>}
            >
              <form onSubmit={submitReply} className="flex flex-col gap-3">
                <label className="flex flex-col gap-1.5">
                  <span className="sr-only">Текст сообщения клиенту</span>
                  <textarea
                    value={draft}
                    onChange={(event) => setDraft(event.target.value)}
                    rows={3}
                    maxLength={4096}
                    placeholder="Написать клиенту от своего имени…"
                    aria-label="Текст сообщения клиенту"
                    className="w-full rounded-md border border-copper/40 bg-raised px-3 py-2 text-sm text-ink"
                  />
                </label>

                {reply.error != null && <ErrorPanel error={reply.error} />}

                <div className="flex flex-wrap items-center gap-2">
                  <Button type="submit" variant="primary" loading={reply.isPending}>
                    {data.isPaused ? 'Отправить клиенту' : 'Поставить на паузу и отправить клиенту'}
                  </Button>

                  {data.isPaused && (
                    <Button loading={setPaused.isPending} onClick={() => setPaused.mutate(false)}>
                      Вернуть агенту
                    </Button>
                  )}
                </div>
              </form>
            </Panel>
          )}

          <Panel
            title="Как получился ответ"
            description="Каждое обращение к модели, каждый вызов инструмента и каждая проверка — по шагам, с токенами и стоимостью"
            actions={
              <Button size="sm" variant="ghost" onClick={() => setShowRuns(!showRuns)}>
                {showRuns ? 'Свернуть' : 'Показать'}
              </Button>
            }
          >
            {!showRuns ? (
              <p className="text-sm text-muted">
                Сюда смотрят, когда ответ вышел не таким, как ждали, — или когда надо понять,
                во что он обошёлся.
              </p>
            ) : runs.isLoading ? (
              <p className="flex items-center gap-2 text-sm text-muted">
                <Spinner /> Загружаем шаги…
              </p>
            ) : runs.error ? (
              <ErrorPanel error={runs.error} onRetry={() => void runs.refetch()} />
            ) : (runs.data?.length ?? 0) === 0 ? (
              <p className="text-sm text-muted">
                Агент в этом диалоге ещё не работал — шагов нет.
              </p>
            ) : (
              <ol className="flex flex-col gap-3">
                {runs.data?.map((run) => <RunCard key={run.id} run={run} />)}
              </ol>
            )}
          </Panel>
        </div>

        <div className="flex flex-col gap-5">
          {canAct && (
            <Panel title="Что делать с диалогом">
              <div className="flex flex-col gap-3">
                {data.needsOperator ? (
                  <div className="rounded-md border border-copper/40 bg-copper-soft p-3">
                    <p className="text-sm font-medium text-copper">Диалог ждёт человека</p>
                    {data.handoffReason && (
                      <p className="mt-1 text-xs text-ink-soft">{data.handoffReason}</p>
                    )}
                    <p className="mt-1 text-xs text-muted">
                      {data.handoffByEmail ?? 'неизвестно кто'} ·{' '}
                      {formatRelative(data.handoffAt)}
                    </p>
                    <Button
                      size="sm"
                      className="mt-2"
                      loading={handoff.isPending}
                      onClick={() => handoff.mutate({ needsOperator: false, reason: '' })}
                    >
                      Снять пометку
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-col gap-2">
                    <Field
                      label="Передать человеку"
                      value={handoffReason}
                      onChange={(event) => setHandoffReason(event.target.value)}
                      maxLength={300}
                      placeholder="Просит скидку, нужен ответ руками"
                      hint="Попадёт в очередь «Ждут оператора» с этой причиной"
                    />
                    <Button
                      size="sm"
                      loading={handoff.isPending}
                      onClick={() =>
                        handoff.mutate(
                          { needsOperator: true, reason: handoffReason.trim() },
                          { onSuccess: () => setHandoffReason('') },
                        )
                      }
                    >
                      Пометить
                    </Button>
                  </div>
                )}

                {handoff.error != null && <ErrorPanel error={handoff.error} />}

                <div className="border-t border-line-soft pt-3">
                  {data.outcome ? (
                    <div className="rounded-md border border-line bg-raised p-3">
                      <p className="text-sm font-medium text-ink">
                        {CONVERSATION_OUTCOME_LABELS[data.outcome]}
                      </p>
                      {data.outcomeNote && (
                        <p className="mt-1 text-xs text-ink-soft">{data.outcomeNote}</p>
                      )}
                      <p className="mt-1 text-xs text-muted">
                        {data.outcomeSource === 'agent'
                          ? 'поставил агент'
                          : (data.outcomeByEmail ?? 'неизвестно кто')}{' '}
                        · {formatRelative(data.outcomeAt)}
                      </p>
                      <Button
                        size="sm"
                        className="mt-2"
                        loading={outcome.isPending}
                        onClick={() => outcome.mutate({ outcome: null, note: '' })}
                      >
                        Снять исход
                      </Button>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-2">
                      <Field
                        label="Чем закончился разговор"
                        value={outcomeNote}
                        onChange={(event) => setOutcomeNote(event.target.value)}
                        maxLength={300}
                        placeholder="Согласился на звонок, просил после 18:00"
                        hint="Исход останавливает агента: разговор закончен или перешёл к человеку"
                      />
                      <div className="flex flex-wrap gap-2">
                        {CONVERSATION_OUTCOMES.map((value) => (
                          <Button
                            key={value}
                            size="sm"
                            variant={value === 'handedToCallCenter' ? 'primary' : 'ghost'}
                            loading={outcome.isPending}
                            onClick={() =>
                              outcome.mutate(
                                { outcome: value, note: outcomeNote.trim() },
                                { onSuccess: () => setOutcomeNote('') },
                              )
                            }
                          >
                            {CONVERSATION_OUTCOME_LABELS[value]}
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}

                  {outcome.error != null && <ErrorPanel error={outcome.error} />}
                </div>

                <div className="flex flex-wrap gap-2 border-t border-line-soft pt-3">
                  {data.state === 'open' &&
                    (data.isPaused ? (
                      /**
                       * Пара к «Поставить на паузу». Раньше снять её
                       * можно было только кнопкой внутри формы ответа
                       * оператора — то есть найти её удавалось не сразу,
                       * а ищут её как раз тогда, когда диалог стоит и
                       * клиент ждёт.
                       */
                      <Button
                        size="sm"
                        loading={setPaused.isPending}
                        onClick={() => setPaused.mutate(false)}
                      >
                        Вернуть агенту
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        loading={setPaused.isPending}
                        onClick={() => setPaused.mutate(true)}
                      >
                        Поставить на паузу
                      </Button>
                    ))}

                  {data.state === 'open' ? (
                    <Button
                      size="sm"
                      variant="danger"
                      loading={setState.isPending}
                      onClick={() => setState.mutate('closed')}
                    >
                      Закрыть диалог
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      loading={setState.isPending}
                      onClick={() => setState.mutate('open')}
                    >
                      Открыть заново
                    </Button>
                  )}
                </div>

                {(setPaused.error ?? setState.error) != null && (
                  <ErrorPanel error={setPaused.error ?? setState.error} />
                )}
              </div>
            </Panel>
          )}

          <Panel title="Кто отвечает">
            {agent.isLoading ? (
              <p className="text-sm text-muted">Загружаем…</p>
            ) : agent.data?.agentVersionId ? (
              <>
                <p className="text-sm text-ink">{agent.data.agentName}</p>
                <p className="mt-0.5 text-xs text-muted">
                  версия {agent.data.agentVersion} — закреплена за этим диалогом
                </p>
                <p className="mt-2 text-xs text-muted">
                  Публикация новой версии агента на этот диалог не подействует: он до конца
                  идёт на той, на которой начался.
                </p>
              </>
            ) : (
              /**
               * Причина отказа показывается дословно, как её вернул
               * сервер. Обобщать её до «агент не отвечает» значило бы
               * стереть разницу между «каналу не назначен агент» и
               * «у агента нет опубликованной версии» — это чинится
               * в разных местах.
               */
              <p className="text-sm text-copper">
                {agent.data?.reason ?? 'версия агента не закреплена'}
              </p>
            )}
          </Panel>

          {/*
            Заявка показывается только когда из первого сообщения хоть
            что-то разобралось. Пустая панель с тремя прочерками сообщала
            бы «здесь должно быть, но нет» на каждом диалоге, заведённом
            руками, — а таких большинство, пока канал тестовый.
          */}
          {(data.productName || data.subid) && (
            <Panel title="Заявка">
              <dl className="flex flex-col gap-1.5 text-sm">
                <Row label="Товар" value={data.productName ?? 'не определён'} />
                <Row label="Источник" value={data.subid ?? 'нет'} />
                <Row label="Группа оффера" value={data.offerGroupId ?? 'нет'} />
              </dl>
            </Panel>
          )}

          <Panel title="Диалог">
            <dl className="flex flex-col gap-1.5 text-sm">
              <Row label="Сообщений" value={String(data.messageCount)} />
              <Row label="Последнее от клиента" value={formatRelative(data.lastInboundAt)} />
              <Row label="Последнее от агента" value={formatRelative(data.lastBotMessageAt)} />
              <Row
                label="Окно 24 часа"
                value={
                  data.serviceWindowExpiresAt
                    ? formatDateTime(data.serviceWindowExpiresAt)
                    : 'у канала нет'
                }
              />
              <Row label="Начат" value={formatDateTime(data.createdAt)} />
            </dl>
          </Panel>
        </div>
      </div>
    </>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-muted">{label}</dt>
      <dd className="text-right text-ink-soft">{value}</dd>
    </div>
  )
}

/**
 * Один прогон агента — попытка ответить на одно сообщение клиента.
 *
 * Шаги показаны все и подряд, включая те, где ничего не произошло.
 * Прятать их было бы аккуратнее на вид, но именно они отвечают на самый
 * частый вопрос — «а проверка вообще работала?»: шаг «проверка без
 * модели» говорит, что к агенту не привязан промпт роли, а «проверка
 * выключена» — что её отключили в настройках. Спрятав их, отличить
 * это от «модель проверила и пропустила» стало бы нечем.
 */
function RunCard({ run }: { run: AgentRunView }) {
  const tone =
    run.status === 'answered'
      ? 'ok'
      : run.status === 'failed' || run.status === 'rejected'
        ? 'bad'
        : 'neutral'

  return (
    <li className="rounded-md border border-line bg-surface px-3.5 py-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={tone}>{AGENT_RUN_STATUS_LABELS[run.status]}</Badge>
          <span className="font-mono text-xs text-muted">{run.model}</span>
        </div>
        <span className="text-xs text-muted">{formatDateTime(run.createdAt)}</span>
      </div>

      {run.reason && <p className="mt-1.5 text-sm text-copper">{run.reason}</p>}

      <p className="mt-1.5 text-xs text-muted">
        {run.iterations} {plural(run.iterations, 'обращение', 'обращения', 'обращений')} к модели ·{' '}
        {(run.durationMs / 1_000).toFixed(1)} с · {run.inputTokens} + {run.outputTokens} токенов ·{' '}
        {/*
          Прочерк, а не ноль: ноль означал бы «бесплатно», а на деле цена
          хотя бы одной из участвовавших моделей не задана в настройках.
        */}
        {run.costUsd === null ? 'цена не задана' : `$${run.costUsd.toFixed(4)}`}
      </p>

      {run.steps.length > 0 && (
        <ol className="mt-2.5 flex flex-col gap-1 border-t border-line-soft pt-2.5">
          {run.steps.map((step) => (
            <li key={step.index} className="flex flex-wrap items-baseline gap-x-2 text-xs">
              <span className="w-4 shrink-0 text-right tabular-nums text-muted">
                {step.index + 1}
              </span>
              <span className={step.ok ? 'text-ink-soft' : 'text-crimson'}>{stepTitle(step)}</span>
              <span className="text-muted">{step.detail}</span>
              {(step.inputTokens !== null || step.outputTokens !== null) && (
                <span className="text-muted tabular-nums">
                  · {step.inputTokens ?? 0} + {step.outputTokens ?? 0} токенов
                </span>
              )}
            </li>
          ))}
        </ol>
      )}
    </li>
  )
}

const STEP_KIND: Record<AgentRunView['steps'][number]['kind'], string> = {
  model: 'модель',
  tool: 'инструмент',
  gate: 'проверка',
}

/**
 * Подпись шага.
 *
 * Вид приписывается к имени, потому что сами имена без него непонятны:
 * `gpt-6-sol` и `hand_to_call_center` ничего не говорят тому, кто
 * открыл диалог впервые. Исключение — проверки: их имена уже назывные
 * («готовность к звонку», «длина»), и часть из них начинается со слова
 * «проверка», отчего приписка давала бы «проверка проверка моделью».
 */
function stepTitle(step: AgentRunView['steps'][number]): string {
  if (step.kind !== 'gate') return `${STEP_KIND[step.kind]} ${step.name}`

  return step.name.startsWith('проверка') ? step.name : `проверка: ${step.name}`
}

const SENDER_LABEL: Record<MessageView['sender'], string> = {
  customer: 'Клиент',
  ai: 'Агент',
  operator: 'Оператор',
  system: 'Система',
}

const STATUS_LABEL: Record<MessageView['status'], string> = {
  pending: 'отправляется',
  sent: 'отправлено',
  delivered: 'доставлено',
  viewed: 'прочитано',
  failed: 'не отправлено',
  received: '',
}

/**
 * Реплика в переписке.
 *
 * Автор подписан словом, а не только выравниванием и цветом. Выравнивание
 * различает две стороны, но у нас их три: клиент, агент и оператор, — и
 * перепутать агента с оператором при разборе жалобы значит разбираться
 * не с тем.
 */
function MessageBubble({ message }: { message: MessageView }) {
  const mine = message.direction === 'outbound'

  return (
    <li className={cn('flex', mine ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'max-w-[85%] rounded-md border px-3 py-2',
          message.sender === 'customer' && 'border-line bg-raised',
          message.sender === 'ai' && 'border-accent/30 bg-accent-soft',
          message.sender === 'operator' && 'border-copper/40 bg-copper-soft',
          message.sender === 'system' && 'border-line-soft bg-surface',
          message.status === 'failed' && 'border-crimson/40 bg-crimson-soft',
        )}
      >
        <p className="text-xs font-medium text-muted">{SENDER_LABEL[message.sender]}</p>
        <p className="mt-0.5 text-sm whitespace-pre-wrap text-ink">
          {message.body || '(без текста)'}
        </p>
        <p className="mt-1 text-[11px] text-muted" title={formatDateTime(message.createdAt)}>
          {formatDateTime(message.createdAt)}
          {STATUS_LABEL[message.status] && ` · ${STATUS_LABEL[message.status]}`}
          {message.errorCode && ` · ${message.errorCode}`}
        </p>
      </div>
    </li>
  )
}
